/**
 * @fileoverview AR-924 — HTTP `TeacherDeliverablePort` against /api/arc_mosaico/task_track/deliverable*.
 * `helpers/api` decamelizes JSON and query params but NOT multipart, so the FormData keys are snake_case.
 */

import axios from 'axios'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import type { TaskTrackDeliverable } from '../../types'
import {
  TeacherDeliverableError,
  type BulkUploadResult,
  type TeacherDeliverablePort,
  type UploadFilesArgs,
  type UploadFilesForStudentsArgs,
  type UploadProgress,
} from '../ports'
import { toTeacherDeliverableError } from './httpErrors'

const UNAUTHORIZED = 401
const PERMISSION_ERROR_PAGE = '/error_permission'
const MULTIPART = { 'Content-Type': 'multipart/form-data' }

type Response = { status: number; data: unknown }

/** `helpers/api` RESOLVES 401s instead of throwing — mirror `assertAuthenticated` of the service. */
function payloadOf<T>(response: Response): T {
  if (response.status === UNAUTHORIZED) {
    window.location.href = PERMISSION_ERROR_PAGE
    throw new TeacherDeliverableError('unknown', ['Unauthorized'])
  }
  return response.data as T
}

async function call<T = TaskTrackDeliverable>(request: () => Promise<Response>): Promise<T> {
  try {
    return payloadOf<T>(await request())
  } catch (error) {
    throw toTeacherDeliverableError(error)
  }
}

const ids = (sessionMeetingActivityId: string, studentId: string) => ({ sessionMeetingActivityId, studentId })

function uploadForm({ activityId, studentId, files }: UploadFilesArgs): FormData {
  const form = new FormData()
  form.append('session_meeting_activity_id', activityId)
  form.append('student_id', studentId)
  files.forEach(file => form.append('files[]', file, file.name))
  return form
}

function groupUploadForm({ activityId, studentIds, files }: UploadFilesForStudentsArgs): FormData {
  const form = new FormData()
  form.append('session_meeting_activity_id', activityId)
  studentIds.forEach(studentId => form.append('student_ids[]', studentId))
  files.forEach(file => form.append('files[]', file, file.name))
  return form
}

/** axios 0.21 has no AbortSignal support — bridge it to a CancelToken. */
function cancelTokenFor(signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  signal?.addEventListener('abort', () => source.cancel())
  return source.token
}

function uploadConfig(signal?: AbortSignal, onProgress?: (progress: UploadProgress) => void) {
  return {
    headers: MULTIPART,
    cancelToken: cancelTokenFor(signal),
    onUploadProgress: (event: ProgressEvent) => onProgress?.({ loaded: event.loaded, total: event.total }),
  }
}

export const httpTeacherDeliverableAdapter: TeacherDeliverablePort = {
  fetch: (activityId, studentId) => call(() =>
    api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.deliverable, { params: ids(activityId, studentId) })),

  uploadFiles: args => call(() =>
    api.post(
      ARC_MOSAICO_ENDPOINTS.taskTrack.deliverableFiles,
      uploadForm(args),
      uploadConfig(args.signal, args.onProgress),
    )),

  uploadFilesForStudents: args => call<BulkUploadResult>(() =>
    api.post(
      ARC_MOSAICO_ENDPOINTS.taskTrack.deliverableFilesBulk,
      groupUploadForm(args),
      uploadConfig(args.signal, args.onProgress),
    )),

  removeFile: (activityId, studentId, fileId) => call(() =>
    api.delete(ARC_MOSAICO_ENDPOINTS.taskTrack.deliverableFile(fileId), { params: ids(activityId, studentId) })),

  addLink: (activityId, studentId, url) => call(() =>
    api.post(ARC_MOSAICO_ENDPOINTS.taskTrack.deliverableLinks, { ...ids(activityId, studentId), url })),

  removeLink: (activityId, studentId, linkId) => call(() =>
    api.delete(ARC_MOSAICO_ENDPOINTS.taskTrack.deliverableLink(linkId), { params: ids(activityId, studentId) })),

  updateWorkCompletedAt: (activityId, studentId, workCompletedAt) => call(() =>
    api.patch(ARC_MOSAICO_ENDPOINTS.taskTrack.deliverable, { ...ids(activityId, studentId), workCompletedAt })),
}
